import React from 'react';
import { MathJax, MathJaxContext } from 'better-react-mathjax';

export interface SharedReviewAnswer {
  questionId: number;
  questionText: string;
  questionImage?: string;
  questionType?: string;
  studentAnswerDisplay: string;
  correctAnswerDisplay: string;
  options: string[];
  marks: number;
  isCorrect: boolean;
  teacherComment?: string;
  status?: string;
  marksAwarded?: number;
}

interface SharedReviewScreenProps {
  studentName: string;
  studentEmail: string;
  attemptNumber: number;
  status: string;
  startedAt: string;
  submittedAt?: string;
  answers: SharedReviewAnswer[];
  comment: string;
  saving: boolean;
  notice: { type: 'success' | 'error'; message: string } | null;
  onCommentChange: (value: string) => void;
  onQuestionCommentChange: (questionId: number, value: string) => void;
  onQuestionCorrectChange?: (questionId: number, isCorrect: boolean) => void;
  onQuestionMarksChange?: (questionId: number, marks: number) => void;
  onSave: () => void;
  onBack: () => void;
  showReviewControls?: boolean;
}

const SharedReviewScreen: React.FC<SharedReviewScreenProps> = ({
  studentName,
  studentEmail,
  attemptNumber,
  status,
  startedAt,
  submittedAt,
  answers,
  comment,
  saving,
  notice,
  onCommentChange,
  onQuestionCommentChange,
  onQuestionCorrectChange,
  onQuestionMarksChange,
  onSave,
  onBack,
  showReviewControls = false,
}) => {
  return (
    <MathJaxContext>
      <div className="bg-white p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{studentName}</h1>
            <p className="text-gray-600">{studentEmail}</p>
            <p className="mt-2 text-sm text-gray-500">
              Attempt #{attemptNumber} • Status: <span className="font-semibold">{status}</span>
            </p>
            <p className="text-sm text-gray-500">
              Started: {new Date(startedAt).toLocaleString()}
              {submittedAt ? ` • Submitted: ${new Date(submittedAt).toLocaleString()}` : ''}
            </p>
          </div>
          <button
            onClick={onBack}
            className="rounded bg-gray-200 px-4 py-2 hover:bg-gray-300"
          >
            ← Back
          </button>
        </div>

        <div className="space-y-8">
          {answers.map((answer, idx) => (
            <div key={answer.questionId} className="rounded-lg border bg-gray-50 p-4">
              <div className="flex items-start gap-4">
                <span className="min-w-fit text-lg font-semibold">Q{idx + 1}</span>
                <div className="flex-1">
                  <div className="mb-4">
                    <MathJax>{answer.questionText}</MathJax>
                    {answer.questionImage && (
                      <img src={answer.questionImage} alt="Question" className="mt-2 max-h-48" />
                    )}
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className="rounded border bg-white p-3">
                      <h4 className="mb-2 text-sm font-semibold text-gray-700">Student&apos;s Answer</h4>
                      {answer.options.length > 0 ? (
                        <div className="space-y-1">
                          {answer.options.map((opt, i) => {
                            const optLetter = String.fromCharCode(65 + i);
                            const isSelected = answer.studentAnswerDisplay === optLetter;
                            return (
                              <label key={i} className="flex items-center gap-2">
                                <input type="radio" checked={isSelected} readOnly className={isSelected ? 'accent-blue-500' : ''} />
                                <span className={isSelected ? 'font-semibold' : ''}>{optLetter}. {opt}</span>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-gray-600">{answer.studentAnswerDisplay || 'No answer'}</p>
                      )}
                    </div>

                    <div className="rounded border border-green-300 bg-green-50 p-3">
                      <h4 className="mb-2 text-sm font-semibold text-gray-700">Correct Answer</h4>
                      {answer.options.length > 0 ? (
                        <div className="space-y-1">
                          {answer.options.map((opt, i) => {
                            const optLetter = String.fromCharCode(65 + i);
                            const isCorrect = answer.correctAnswerDisplay === optLetter;
                            return (
                              <div key={i} className={`flex items-center gap-2 ${isCorrect ? 'font-semibold text-green-700' : 'text-gray-600'}`}>
                                <span>{isCorrect ? '✓' : '•'}</span>
                                <span>{optLetter}. {opt}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-gray-600">{answer.correctAnswerDisplay || 'N/A'}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className={`rounded px-3 py-1 text-sm font-semibold ${answer.isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {answer.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                    </span>
                    <span className="text-sm text-gray-600">Marks: {answer.marks}</span>
                    {answer.status ? <span className="text-sm text-gray-500">{answer.status}</span> : null}
                  </div>

                  {showReviewControls ? (
                    <div className="mt-4 rounded border border-gray-200 bg-white p-3">
                      <div className="mb-3 flex flex-wrap items-center gap-3">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <input
                            type="radio"
                            name={`correct-${answer.questionId}`}
                            checked={answer.isCorrect}
                            onChange={() => onQuestionCorrectChange?.(answer.questionId, true)}
                          />
                          Correct
                        </label>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <input
                            type="radio"
                            name={`correct-${answer.questionId}`}
                            checked={!answer.isCorrect}
                            onChange={() => onQuestionCorrectChange?.(answer.questionId, false)}
                          />
                          Incorrect
                        </label>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <span>Marks</span>
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={answer.marksAwarded ?? answer.marks}
                            onChange={(event) => onQuestionMarksChange?.(answer.questionId, Number(event.target.value || 0))}
                            className="w-24 rounded border px-2 py-1"
                          />
                        </label>
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-4 border-t pt-4">
                    <label className="mb-2 block text-sm font-semibold text-gray-700">Feedback for this question (optional)</label>
                    <textarea
                      value={answer.teacherComment || ''}
                      onChange={(event) => onQuestionCommentChange(answer.questionId, event.target.value)}
                      placeholder="Add specific feedback for this question..."
                      className="w-full rounded border p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 border-t pt-6">
          <h3 className="mb-3 text-lg font-semibold">Teacher Feedback</h3>
          <textarea
            value={comment}
            onChange={(event) => onCommentChange(event.target.value)}
            placeholder="Add comments for the student..."
            className="w-full rounded-lg border p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={4}
          />
          {notice && (
            <div className={`mt-3 rounded-lg border px-4 py-3 text-sm ${notice.type === 'success' ? 'border-green-300 bg-green-50 text-green-800' : 'border-red-300 bg-red-50 text-red-800'}`}>
              {notice.message}
            </div>
          )}
          <button
            onClick={onSave}
            disabled={saving}
            className="mt-3 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:bg-gray-400"
          >
            {saving ? 'Saving...' : 'Save Review'}
          </button>
        </div>
      </div>
    </MathJaxContext>
  );
};

export default SharedReviewScreen;
